/**
 * `/api/agents/health` — the fleet health view's single read (RO-3-13).
 *
 * ## Why this is a REWRITE and not a port
 *
 * `excellent/app/agents/health/page.tsx` is 464 lines of server JSX that calls
 * `readSystemStatus()` inline and renders the entire page from the result.
 * There is no client component to hand a prop to, so there is nothing to
 * "port" — the read must become an endpoint before the render can become a
 * client route. That is the shape of every one of RO-3-13's rewrites.
 *
 * ## Decision 1 — the failure is payload, not an HTTP error
 *
 * `readSystemStatus()` returns a discriminated
 * `{ ok: true, status } | { ok: false, error }`. That union is preserved ACROSS
 * THE WIRE rather than collapsed into a 5xx, because the page's whole purpose
 * (its own header: "ONE proof-based view") is to render a failed probe as
 * *evidence*. A health page that 500s when the health probe fails tells the
 * operator strictly less than one that says "the probe could not run, and here
 * is why". So the transport is always 200 and the union is the body.
 *
 * ## Decision 2 — the view types are MIRRORED here, not imported
 *
 * `excellent/src/agents/health/status.ts` is the module that owns
 * `SystemStatusView`, and its first line is `import "server-only"` — importing
 * it from a client route is the exact hazard that makes every route 500 at once
 * (and it value-imports `node:child_process`). `packages/ui` also sits BELOW
 * `excellent/src` in the dependency order, so it could not import it even if
 * that were safe.
 *
 * So the shapes below are a faithful structural mirror, and the mirror is
 * load-bearing in one direction: the server handler's return value is checked
 * against `AgentHealthResult` at the `mount()` call, so if the oracle's shape
 * drifts, `tsc` fails at the endpoint rather than the browser failing at
 * runtime. Comments that explain WHY a field is nullable or optional are copied
 * with the field, because those are the parts a re-derivation gets wrong.
 *
 * ## Decision 3 — `z.custom`, not a field-by-field zod re-derivation
 *
 * The oracle (`packages/excellent-mcp/src/system-status.ts`) adds fields
 * additively without bumping `schemaVersion` — `dod` is the documented
 * precedent. A strict zod object here would STRIP the next such field silently,
 * turning an additive CLI change into a blank card with no error anywhere. The
 * envelope is validated; the view is `z.custom`, matching how `agent-goals.ts`
 * types `AgentGoalCreateResult`.
 */

import { z } from "zod";
import type { Operation } from "../types";

export interface SubsystemStatusDto {
  key: string;
  label: string;
  enabled: boolean;
  defaultOn: boolean;
  atDefault: boolean;
  /**
   * #45392 - mirrors the oracle additive operating/inert (#45382, no schema
   * bump). A payload from a dist built before #45382 has no such key, so both
   * are typed optional - required would put a runtime TypeError on the health
   * page instead of a missing card. operating:null means not probed - never
   * assume either direction from it, and never render it as green (the
   * fail-open this surface exists to prevent); but null is also never inert
   * (gate-asserted per subsystem in test-system-status.mjs).
   */
  operating?: boolean | null;
  /** enabled === true AND operating === false - switched on, demonstrably not running. */
  inert?: boolean;
  settingsHref: string;
  note?: string;
}

export interface GateContractDto {
  suiteCount: number | null;
  recursiveDone: string[];
  runsVia: string;
}

export interface VaultOpsEventDto {
  kind: string;
  severity: string;
  title: string;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
}

export interface VaultStateDto {
  openEvents: VaultOpsEventDto[];
  /** null = the ops table could not be read. Render that as unknown, NOT green. */
  clear: boolean | null;
}

export interface DodReceiptDto {
  taskId: number;
  command: string;
  exitCode: number;
  mode: string | null;
  binding: string;
  finishedAt: string | null;
}

export interface DodRatchetStateDto {
  criteria: { bound: number; tasks: number; gateRefs: number };
  receipts: {
    total: number;
    passing: number;
    failing: number;
    delta: number;
    unbound: number;
  };
  recent: DodReceiptDto[];
  openOps: number | null;
  /** false = the criteria/receipt tables could not be read. Not the same as empty. */
  readable: boolean;
}

export interface LoopLivenessStatusDto {
  /** true iff the verification-loop flags are OFF|bypassed while consequence-tagged work flows. */
  red: boolean;
  /** which flag(s) read off: learn-disabled | claim-check-bypassed. */
  reasons: string[];
  /** positive DB evidence count of age-bounded consequence-tagged live tasks. */
  taggedFlowCount: number;
  /** false when the tasks table could not be read — NOT the same as red=false. */
  readable: boolean;
}

/**
 * The verification canary's two axes, mirrored from the oracle's `CanaryStatus`
 * (packages/excellent-mcp/src/canary-status.ts).
 *
 * Two axes rather than one bit: "nothing is scheduled to run the loop" and
 * "the loop ran, 65 hours ago" are different diagnoses with different repairs,
 * and collapsing them is how a never-armed scheduler hides inside a
 * temporarily-late one.
 *
 * Every rendered string here — `headline`, `detail`, and each axis's `detail`
 * — is produced by the ORACLE and carried whole, so the page prints them
 * rather than re-deriving a second copy of the wording (#45480).
 */
export interface CanaryStatusDto {
  scheduler: {
    /** `unknown` = the job exists but the scheduler could not be asked. Never render as armed. */
    state: "armed" | "unarmed" | "unknown";
    label: string;
    plistPath: string;
    plistPresent: boolean;
    declaredIntervalSeconds: number | null;
    runAtLoad: boolean | null;
    loaded: boolean | null;
    /** the job's declared log files exist — the evidence separating never-armed from disarmed. */
    logPresent: boolean | null;
    intervalAgreesWithReading: boolean | null;
    reasonCodes: string[];
    detail: string;
  };
  loop: {
    /** `never-ran` is NOT a flavour of `stale`; a fresh install is not an outage. */
    state: "live" | "stale" | "never-ran" | "unreadable";
    runCount: number | null;
    lastRunAt: string | null;
    secondsSinceLastRun: number | null;
    missedIntervals: number | null;
    intervalSeconds: number;
    staleAfterSeconds: number;
    /** outages already in the recorded series — gaps nobody saw at the time. */
    historicalStaleWindows: number | null;
    reasonCodes: string[];
    detail: string;
  };
  tile: string;
  headline: string;
  detail: string;
  healthy: boolean;
}

export interface SystemStatusViewDto {
  schemaVersion: number;
  generatedAt: string;
  node: string;
  ok: boolean;
  git: {
    root: string | null;
    main: string | null;
    tier: {
      ref: string;
      sha: string | null;
      mainIsAncestor: boolean | null;
      aheadOfMain: number | null;
      behindMain: number | null;
    };
    mirror: {
      originMain: string | null;
      mainAheadOfOrigin: number | null;
      ffable: boolean | null;
    };
  };
  workers: { activeLeases: number; alive: number; dead: number };
  strandedDone: { count: number; taskIds: number[] };
  goals: { active: number; done: number; abandoned: number; other: number };
  load: { perCore: number; ceilingPerCore: number; exceeds: boolean };
  usageWindow: {
    state: string;
    bindingPct: number | null;
    sessionPct: number | null;
    weekPct: number | null;
    source: string;
  };
  braid: { conflicts: number | null };
  tickSingleton: { held: boolean; holderPid: number | null; holderAlive: boolean };
  vault: VaultStateDto;
  subsystems: SubsystemStatusDto[];
  gates: GateContractDto;
  /**
   * OPTIONAL on purpose (copied from the oracle's own note): `dod` was added
   * additively and did NOT bump `schemaVersion`, so a payload from a dist built
   * before #44086 has no `dod` key at all. Typing it required would put a
   * runtime TypeError on the health page instead of a missing card.
   */
  dod?: DodRatchetStateDto;
  /**
   * #44812 — the names of the health clauses currently FAILING, empty iff `ok`.
   * Carried so the page renders WHICH clause fired instead of a wordless
   * "Attention" (the read is invertible: `ok === (notOk.length === 0)`).
   */
  notOk?: string[];
  /**
   * #45480 — the rendered detail line per failing clause, index-aligned to
   * `notOk`. Mirrors the oracle's additive `notOkDetail` so the page renders
   * the live-count detail instead of a hand-maintained clause map.
   */
  notOkDetail?: string[];
  /**
   * #45025/#45273 — kill-switch liveness: the loop alarm. ADDITIVE (no schema
   * bump), so it is optional on the wire exactly like `dod` — a payload from a
   * dist built before it shipped has no `loopLiveness` key. The view must guard
   * on presence rather than assume it.
   */
  loopLiveness?: LoopLivenessStatusDto;
  /**
   * The verification canary. ADDITIVE (no schema bump), so it is optional on
   * the wire exactly like `loopLiveness` — a payload from a dist built before
   * it shipped has no `canary` key. The view guards on presence and renders
   * absence as UNMEASURED, never as healthy: a canary tile that reads green
   * because there is no reading is the failure this block exists to end.
   */
  canary?: CanaryStatusDto;
}

export type AgentHealthResult =
  | { ok: true; status: SystemStatusViewDto }
  | { ok: false; error: string };

const getOp: Operation<void, void, void, AgentHealthResult> = {
  method: "GET",
  path: () => "/api/agents/health",
  response: z.custom<AgentHealthResult>(),
};

export const agentHealth = {
  get: getOp,
};
